import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { DataType } from '@api/devices';
import { RestrictedElementDirective } from '@auth';
import { ActionPointValidationConfig, type ModelActionPoint } from '@devices/models';
import { SliderComponent } from '@ui';

@Component({
  imports: [
    MatInput,
    FormsModule,
    MatError,
    MatFormField,
    MatIcon,
    MatOption,
    MatSelect,
    MatSuffix,
    ReactiveFormsModule,
    RestrictedElementDirective,
    SliderComponent,
  ],
  selector: 'avs-action-point-value-selector',
  templateUrl: './action-point-value-selector.component.html',
})
export class ActionPointValueSelectorComponent {
  actionPoint = input.required<ModelActionPoint>();
  valueChanged = output<(string)>();

  protected hasPresetValue = signal(false);
  protected hasSelectableValues = signal(false);
  protected dropdownValues = signal<string[]>([]);
  protected usesSlider = signal(false);
  protected isNumericType = signal(false);

  protected placeholderText = computed(
    () => `Enter the ${this.actionPoint().valueDataType} value to execute this action`,
  );

  protected form: FormGroup;
  protected fb = inject(FormBuilder);

  constructor() {
    this.form = this.fb.group({
      value: [''],
    });

    this.form.get('value')!.valueChanges.subscribe(v => {
      this.emitValue(v);
    });

    effect(() => {
      const ap = this.actionPoint();
      const rules = ap.valueValidationRule;
      const type = ap.valueDataType;
      const config = rules?.config;
      const isEnum = config === ActionPointValidationConfig.ENUM;
      const isMinMax = config === ActionPointValidationConfig.MIN_MAX;
      const isNumeric = type === DataType.INTEGER || type === DataType.FLOAT;

      this.form.reset({ value: '' });
      this.isNumericType.set(isNumeric);
      this.hasPresetValue.set(!!rules?.values);
      this.usesSlider.set(false);
      this.hasSelectableValues.set(false);
      this.dropdownValues.set([]);

      this.initializeFromValidation({
        isEnum,
        isMinMax,
        isNumeric,
        rules,
        type,
      });
    });
  }

  protected patchValue(value: string) {
    this.form.patchValue({ value: `${value}` });
  }

  private initializeFromValidation(args: {
    isEnum: boolean;
    isMinMax: boolean;
    isNumeric: boolean;
    rules: ModelActionPoint['valueValidationRule'] | null;
    type: DataType;
  }) {
    const { isNumeric, isEnum, isMinMax, rules, type } = args;

    if (this.hasPresetValue()) {
      if (isNumeric && isEnum) {
        this.initEnumNumeric(rules?.values ?? '');
      } else {
        this.patchValue(rules?.values ?? '');
      }

      return;
    }

    if (type === DataType.BOOLEAN) {
      this.initBooleanDefaults();
    }

    if (isNumeric && isMinMax) {
      this.initMinMaxNumeric(rules);
    }
  }

  private initEnumNumeric(valuesCsv: string) {
    const arr = valuesCsv.split(',').map(v => v.trim()).filter(Boolean);

    if (arr.length > 1) {
      this.dropdownValues.set(arr);
      this.hasSelectableValues.set(true);
      this.patchValue(arr[0]);
    } else if (arr.length === 1) {
      this.patchValue(arr[0]);
    }
  }

  private initBooleanDefaults() {
    this.dropdownValues.set(['', 'true', 'false']);
    this.hasSelectableValues.set(true);
  }

  private initMinMaxNumeric(rules: ModelActionPoint['valueValidationRule'] | null) {
    this.usesSlider.set(true);
    const defaultValue = rules?.min;

    if (defaultValue !== undefined) {
      this.patchValue(defaultValue.toString());
    }
  }

  private emitValue(v: string) {
    this.valueChanged.emit(v);
  }
}
